import { useState } from "react";
import { ArrowRight, Lock } from "lucide-react";
import { projects } from "@/content/projects";
import { site } from "@/content/site";

export function LeadForm({ compact = false, selectedProject = "" }: { compact?: boolean; selectedProject?: string }) {
  const [error, setError] = useState("");

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = String(formData.get("name") || "").trim();
    const phone = String(formData.get("phone") || "").trim();
    const project = String(formData.get("project") || "").trim();
    const message = String(formData.get("message") || "").trim();

    if (!name || !phone) {
      setError("Please enter your name and phone number.");
      return;
    }

    setError("");
    const text = encodeURIComponent(
      `Hello Swargaseema, I would like to book a site visit.\nName: ${name}\nPhone: ${phone}\nProject: ${project}\nRequirement: ${message || "Please call me with details."}`
    );
    window.location.href = `https://wa.me/91${site.phone.replaceAll(" ", "")}?text=${text}`;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`grid gap-5 rounded-2xl bg-white p-5 shadow-[0_14px_36px_rgba(23,60,49,0.09)] ${compact ? "" : "border border-[var(--line)]"} sm:grid-cols-2 sm:p-7`}
    >
      <div className="relative min-w-0">
        <input
          id="name"
          name="name"
          type="text"
          placeholder=" "
          className="peer w-full rounded-xl border border-[var(--line)] bg-[#fbfcfb] px-4 pb-2 pt-6 text-[var(--ink)] outline-none transition-all focus:border-[var(--gold)] focus:ring-1 focus:ring-[var(--gold)]"
        />
        <label htmlFor="name" className="pointer-events-none absolute left-4 top-4 text-sm font-bold text-[var(--muted)] transition-all peer-placeholder-shown:translate-y-1 peer-placeholder-shown:text-base peer-focus:-translate-y-2.5 peer-focus:text-xs peer-focus:text-[var(--gold-strong)] -translate-y-2.5 text-xs">
          Full Name
        </label>
      </div>
      <div className="relative min-w-0">
        <input
          id="phone"
          name="phone"
          type="tel"
          placeholder=" "
          className="peer w-full rounded-xl border border-[var(--line)] bg-[#fbfcfb] px-4 pb-2 pt-6 text-[var(--ink)] outline-none transition-all focus:border-[var(--gold)] focus:ring-1 focus:ring-[var(--gold)]"
        />
        <label htmlFor="phone" className="pointer-events-none absolute left-4 top-4 text-sm font-bold text-[var(--muted)] transition-all peer-placeholder-shown:translate-y-1 peer-placeholder-shown:text-base peer-focus:-translate-y-2.5 peer-focus:text-xs peer-focus:text-[var(--gold-strong)] -translate-y-2.5 text-xs">
          Phone Number
        </label>
      </div>
      <div className="relative min-w-0 sm:col-span-2">
        <select
          id="project"
          name="project"
          defaultValue={selectedProject || projects[0]?.name}
          className="peer w-full rounded-xl border border-[var(--line)] bg-[#fbfcfb] px-4 pb-2 pt-6 text-[var(--ink)] outline-none transition-all focus:border-[var(--gold)] focus:ring-1 focus:ring-[var(--gold)]"
        >
          {projects.map((project) => (
            <option key={project.slug}>{project.name}</option>
          ))}
        </select>
        <label htmlFor="project" className="pointer-events-none absolute left-4 top-1.5 text-xs font-bold text-[var(--muted)] transition-all peer-focus:text-[var(--gold-strong)]">
          Interested Project
        </label>
      </div>
      <div className="relative min-w-0 sm:col-span-2">
        <textarea
          id="message"
          name="message"
          rows={4}
          placeholder=" "
          className="peer w-full rounded-xl border border-[var(--line)] bg-[#fbfcfb] px-4 pb-2 pt-6 text-[var(--ink)] outline-none transition-all focus:border-[var(--gold)] focus:ring-1 focus:ring-[var(--gold)]"
        />
        <label htmlFor="message" className="pointer-events-none absolute left-4 top-4 text-sm font-bold text-[var(--muted)] transition-all peer-placeholder-shown:translate-y-1 peer-placeholder-shown:text-base peer-focus:-translate-y-2.5 peer-focus:text-xs peer-focus:text-[var(--gold-strong)] -translate-y-2.5 text-xs">
          Requirement
        </label>
      </div>
      {error && <p className="text-sm font-bold text-red-700 sm:col-span-2">{error}</p>}
      <button type="submit" className="group relative inline-flex min-h-12 items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-[var(--gold-strong)] to-[#e2c184] px-5 text-sm font-black text-[var(--ink)] shadow-[0_4px_14px_rgba(201,164,94,0.25)] transition-all duration-300 hover:shadow-[0_6px_20px_rgba(201,164,94,0.4)] active:scale-95 sm:col-span-2">
        <span className="relative z-10 flex items-center gap-2 transition-transform duration-300 group-hover:translate-x-0.5">Submit Request <ArrowRight size={18} /></span>
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full transition-transform duration-500 ease-in-out group-hover:translate-x-full" />
      </button>
      <div className="flex items-center justify-center gap-2 text-center text-xs text-[var(--muted)] sm:col-span-2">
        <Lock size={12} /> Your information is secure and will not be shared.
      </div>
    </form>
  );
}
